import { useState, useEffect } from 'react';
import { 
  Gamepad2, 
  Settings, 
  Plus,
  Users,
  Clock,
  DollarSign,
  ChevronLeft,
  ChevronRight,
  Edit,
  History,
  Search,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { gamesAPI, gameHistoryAPI } from '../lib/api';
import GameLobbyPanel from './GameLobbyPanel';
import { formatCurrency, formatDateTime, getStatusColor, getGameVariantLabel } from '../lib/utils';
import { toast } from 'sonner';

const StatusBadge = ({ status }) => {
  const color = getStatusColor(status);
  const colorClasses = {
    success: 'badge-success',
    warning: 'badge-warning',
    danger: 'badge-danger',
    info: 'badge-info',
    neutral: 'badge-neutral',
  };
  
  return (
    <span className={`badge ${colorClasses[color]}`}>
      {status?.replace('_', ' ')}
    </span>
  );
};

export default function GameManagement() {
  const [settings, setSettings] = useState([]);
  const [tables, setTables] = useState([]);
  const [totalTables, setTotalTables] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [limit] = useState(20);
  const [variantFilter, setVariantFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('live');
  const [editDialog, setEditDialog] = useState(false);
  const [selectedSetting, setSelectedSetting] = useState(null);
  const [createTableDialog, setCreateTableDialog] = useState(false);
  const [newTable, setNewTable] = useState({
    variant: 'points_rummy',
    entry_fee: 100,
    max_players: 6,
    is_private: false,
  });

  const fetchSettings = async () => {
    try {
      const response = await gamesAPI.getSettings();
      setSettings(response.data);
    } catch (error) {
      toast.error('Failed to fetch game settings');
    }
  };

  const fetchTables = async () => {
    try {
      setLoading(true);
      const params = {
        skip: page * limit,
        limit,
        variant: variantFilter !== 'all' ? variantFilter : undefined,
        status: statusFilter !== 'all' ? statusFilter : undefined,
      };
      const response = await gamesAPI.getTables(params);
      setTables(response.data.tables);
      setTotalTables(response.data.total);
    } catch (error) {
      toast.error('Failed to fetch tables');
    } finally {
      setLoading(false);
    }
  };

  const [liveTables, setLiveTables] = useState([]);
  const [liveStats, setLiveStats] = useState(null);
  const [platformConfig, setPlatformConfig] = useState(null);
  const [audits, setAudits] = useState([]);
  const [history, setHistory] = useState([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historySearch, setHistorySearch] = useState('');
  const [historyType, setHistoryType] = useState('all');
  const [historyResult, setHistoryResult] = useState('all');

  const fetchLive = async () => {
    try {
      const res = await gamesAPI.getLiveTables();
      setLiveTables(res.data.tables || []);
      setLiveStats(res.data.stats || null);
    } catch {
      toast.error('Failed to fetch live tables');
    }
  };

  const fetchPlatform = async () => {
    try {
      const res = await gamesAPI.getPlatformConfig();
      setPlatformConfig(res.data);
    } catch {
      toast.error('Failed to fetch platform config');
    }
  };

  const fetchAudits = async () => {
    try {
      const res = await gamesAPI.getDeclareAudits({ limit: 30 });
      setAudits(res.data.audits || []);
    } catch {
      toast.error('Failed to fetch declare audits');
    }
  };

  const fetchHistory = async () => {
    try {
      setLoading(true);
      const res = await gameHistoryAPI.getAll({
        skip: page * limit,
        limit,
        search: historySearch.trim() || undefined,
        game_type: historyType !== 'all' ? historyType : undefined,
        result: historyResult !== 'all' ? historyResult : undefined,
      });
      setHistory(res.data.games || []);
      setHistoryTotal(res.data.total || 0);
    } catch {
      toast.error('Failed to fetch game history');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  useEffect(() => {
    if (activeTab === 'tables') return;
    if (activeTab === 'history') fetchHistory();
    if (activeTab === 'live') {
      fetchLive();
      fetchAudits();
      const iv = setInterval(fetchLive, 5000);
      return () => clearInterval(iv);
    }
    if (activeTab === 'platform') fetchPlatform();
  }, [page, variantFilter, statusFilter, activeTab, historySearch, historyType, historyResult]);

  const handleUpdateSetting = async () => {
    try {
      await gamesAPI.updateSettings(selectedSetting.variant, selectedSetting);
      toast.success('Settings updated');
      setEditDialog(false);
      fetchSettings();
    } catch (error) {
      toast.error('Failed to update settings');
    }
  };

  const handleCreateTable = async () => {
    try {
      await gamesAPI.createTable(newTable);
      toast.success('Table created');
      setCreateTableDialog(false);
      setNewTable({ variant: 'points_rummy', entry_fee: 100, max_players: 6, is_private: false });
      fetchTables();
    } catch (error) {
      toast.error('Failed to create table');
    }
  };

  const openEditDialog = (setting) => {
    setSelectedSetting({ ...setting });
    setEditDialog(true);
  };

  return (
    <div data-testid="games-page">
      {/* Header */}
      <div className="page-header">
        <h1 className="page-title">Live Games</h1>
        <p className="page-subtitle">Watch tables in play, review history, and change engine settings</p>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="bg-card border border-border">
          <TabsTrigger value="live" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
            <Users className="w-4 h-4 mr-2" />
            Live
          </TabsTrigger>
          <TabsTrigger value="history" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
            <History className="w-4 h-4 mr-2" />
            History
          </TabsTrigger>
          <TabsTrigger value="tables" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
            <Gamepad2 className="w-4 h-4 mr-2" />
            App lobby
          </TabsTrigger>
          <TabsTrigger value="settings" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
            <Settings className="w-4 h-4 mr-2" />
            Settings
          </TabsTrigger>
          <TabsTrigger value="platform" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
            <DollarSign className="w-4 h-4 mr-2" />
            Platform Config
          </TabsTrigger>
        </TabsList>

        {/* Game Settings Tab */}
        <TabsContent value="settings">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {settings.map((setting) => (
              <div key={setting.variant} className="stat-card" data-testid={`game-setting-${setting.variant}`}>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${setting.enabled ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'}`}>
                      <Gamepad2 className="w-5 h-5" />
                    </div>
                    <div>
                      <h3 className="font-semibold">{getGameVariantLabel(setting.variant)}</h3>
                      <StatusBadge status={setting.enabled ? 'active' : 'blocked'} />
                    </div>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => openEditDialog(setting)}
                    data-testid={`edit-setting-${setting.variant}`}
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                </div>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">Entry Fee Range</p>
                    <p className="font-mono">{formatCurrency(setting.min_entry_fee)} - {formatCurrency(setting.max_entry_fee)}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Rake</p>
                    <p className="font-mono">{setting.rake_percentage}% (Cap: {formatCurrency(setting.rake_cap)})</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Max Players</p>
                    <p className="font-mono">{setting.max_players}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Turn Timer</p>
                    <p className="font-mono">{setting.turn_timer}s</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Drop Penalty</p>
                    <p className="font-mono">{setting.drop_penalty} pts</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="tables">
          <GameLobbyPanel />
        </TabsContent>


        <TabsContent value="history">
          <div className="data-table-container">
            <div className="data-table-header flex flex-wrap gap-3">
              <h3 className="data-table-title">Completed games ({historyTotal})</h3>
              <div className="filter-bar flex flex-wrap gap-2">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input className="pl-9 w-56" placeholder="Player, table, opponent" value={historySearch} onChange={(e) => { setHistorySearch(e.target.value); setPage(0); }} />
                </div>
                <Select value={historyType} onValueChange={(v) => { setHistoryType(v); setPage(0); }}>
                  <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All modes</SelectItem>
                    <SelectItem value="points">Points</SelectItem>
                    <SelectItem value="pool">Pool</SelectItem>
                    <SelectItem value="deals">Deals</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={historyResult} onValueChange={(v) => { setHistoryResult(v); setPage(0); }}>
                  <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All results</SelectItem>
                    <SelectItem value="won">Won</SelectItem>
                    <SelectItem value="lost">Lost</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Table>
              <TableHeader>
                <TableRow className="table-header">
                  <TableHead>When</TableHead>
                  <TableHead>Player</TableHead>
                  <TableHead>Mode</TableHead>
                  <TableHead>Result</TableHead>
                  <TableHead className="text-right">Fee</TableHead>
                  <TableHead>Opponent</TableHead>
                  <TableHead>Table</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={7} className="text-center py-12"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary mx-auto" /></TableCell></TableRow>
                ) : history.length === 0 ? (
                  <TableRow><TableCell colSpan={7} className="text-center py-12 text-muted-foreground">No games found</TableCell></TableRow>
                ) : history.map((g, i) => (
                  <TableRow key={`${g.table_id || ''}-${g.user_id || g.player_phone}-${i}`}>
                    <TableCell className="text-sm">{formatDateTime(g.created_at)}</TableCell>
                    <TableCell className="font-mono text-xs">{g.user_id || g.player_phone}</TableCell>
                    <TableCell className="capitalize">{g.game_type}{g.pool_limit ? ` ${g.pool_limit}` : ''}</TableCell>
                    <TableCell><StatusBadge status={g.result} /></TableCell>
                    <TableCell className="text-right font-mono">{formatCurrency(g.entry_fee)}</TableCell>
                    <TableCell>{g.opponent_name || '—'}</TableCell>
                    <TableCell className="font-mono text-xs">{g.table_id || '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <div className="flex items-center justify-between px-4 py-3 border-t border-border">
              <p className="text-sm text-muted-foreground">
                Showing {historyTotal === 0 ? 0 : page * limit + 1} to {Math.min((page + 1) * limit, historyTotal)} of {historyTotal}
              </p>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0}><ChevronLeft className="w-4 h-4" /></Button>
                <Button variant="outline" size="sm" onClick={() => setPage((p) => p + 1)} disabled={(page + 1) * limit >= historyTotal}><ChevronRight className="w-4 h-4" /></Button>
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="live">
          <div className="space-y-4">
            {liveStats && (
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <div className="stat-card"><p className="text-muted-foreground text-xs">Playing</p><p className="text-xl font-bold">{liveStats.active_games}</p></div>
                <div className="stat-card"><p className="text-muted-foreground text-xs">Waiting</p><p className="text-xl font-bold">{liveStats.waiting_tables}</p></div>
                <div className="stat-card"><p className="text-muted-foreground text-xs">Tables</p><p className="text-xl font-bold">{liveStats.total_tables}</p></div>
                <div className="stat-card"><p className="text-muted-foreground text-xs">Players</p><p className="text-xl font-bold">{liveStats.total_players}</p></div>
                <div className="stat-card"><p className="text-muted-foreground text-xs">WS</p><p className="text-xl font-bold">{liveStats.connections}</p></div>
              </div>
            )}
            <div className="data-table-container">
              <div className="data-table-header flex justify-between items-center">
                <h3 className="data-table-title">In-memory live tables</h3>
                <Button size="sm" variant="outline" onClick={fetchLive}>Refresh</Button>
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>ID</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Players</TableHead>
                    <TableHead>Deal</TableHead>
                    <TableHead>Fee</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {liveTables.length === 0 ? (
                    <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground">No live tables</TableCell></TableRow>
                  ) : liveTables.map((t) => (
                    <TableRow key={t.table_id}>
                      <TableCell className="font-mono text-xs">{t.table_id}</TableCell>
                      <TableCell>{t.game_type}{t.pool_limit ? ` ${t.pool_limit}` : ''}</TableCell>
                      <TableCell><StatusBadge status={t.status} /></TableCell>
                      <TableCell>{t.current_players}/{t.max_players}</TableCell>
                      <TableCell>{t.current_deal}/{t.total_deals}</TableCell>
                      <TableCell>{formatCurrency(t.entry_fee)}</TableCell>
                      <TableCell>
                        <Button size="sm" variant="destructive" onClick={async () => {
                          if (!window.confirm(`Void table ${t.table_id} and refund entry fees?`)) return;
                          try {
                            await gamesAPI.voidTable(t.table_id, 'admin void from panel');
                            toast.success('Table voided & refunded');
                            fetchLive();
                          } catch { toast.error('Void failed'); }
                        }}>Void</Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <div className="data-table-container">
              <h3 className="data-table-title p-4">Recent declare audits</h3>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Time</TableHead>
                    <TableHead>Table</TableHead>
                    <TableHead>Player</TableHead>
                    <TableHead>Valid</TableHead>
                    <TableHead>Reason</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {audits.length === 0 ? (
                    <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground">No audits yet</TableCell></TableRow>
                  ) : audits.map((a, i) => (
                    <TableRow key={i}>
                      <TableCell className="text-xs">{a.timestamp?.slice(0, 19)}</TableCell>
                      <TableCell className="font-mono text-xs">{a.table_id}</TableCell>
                      <TableCell>{a.player_name || a.player_id}</TableCell>
                      <TableCell>{a.meld_valid ? '✓' : (a.structural_ok === false ? 'reject' : '✗')}</TableCell>
                      <TableCell className="text-xs max-w-xs truncate">{a.meld_reason || a.structural_reason}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="platform">
          {platformConfig && (
            <div className="stat-card max-w-xl space-y-4">
              <div className="flex items-center justify-between">
                <Label>Maintenance mode</Label>
                <Switch checked={!!platformConfig.maintenance_mode}
                  onCheckedChange={(v) => setPlatformConfig({ ...platformConfig, maintenance_mode: v })} />
              </div>
              <div className="flex items-center justify-between">
                <Label>Bot fill enabled</Label>
                <Switch checked={!!platformConfig.bot_fill_enabled}
                  onCheckedChange={(v) => setPlatformConfig({ ...platformConfig, bot_fill_enabled: v })} />
              </div>
              <div className="space-y-2">
                <Label>Rake %</Label>
                <Input type="number" value={platformConfig.rake_percentage}
                  onChange={(e) => setPlatformConfig({ ...platformConfig, rake_percentage: parseFloat(e.target.value) })} />
              </div>
              <div className="space-y-2">
                <Label>Bot match delay (seconds)</Label>
                <Input type="number" value={platformConfig.bot_match_delay_seconds}
                  onChange={(e) => setPlatformConfig({ ...platformConfig, bot_match_delay_seconds: parseInt(e.target.value, 10) })} />
              </div>
              <div className="flex items-center justify-between">
                <Label>Tournaments enabled</Label>
                <Switch checked={!!platformConfig.tournaments_enabled}
                  onCheckedChange={(v) => setPlatformConfig({ ...platformConfig, tournaments_enabled: v })} />
              </div>
              <Button onClick={async () => {
                try {
                  await gamesAPI.updatePlatformConfig({
                    maintenance_mode: platformConfig.maintenance_mode,
                    bot_fill_enabled: platformConfig.bot_fill_enabled,
                    rake_percentage: platformConfig.rake_percentage,
                    bot_match_delay_seconds: platformConfig.bot_match_delay_seconds,
                    tournaments_enabled: platformConfig.tournaments_enabled,
                  });
                  toast.success('Platform config saved — engine will use new values');
                  fetchPlatform();
                } catch { toast.error('Save failed'); }
              }}>Save platform config</Button>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Edit Setting Dialog */}
      <Dialog open={editDialog} onOpenChange={setEditDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit {getGameVariantLabel(selectedSetting?.variant)}</DialogTitle>
          </DialogHeader>
          {selectedSetting && (
            <div className="space-y-4 py-4">
              <div className="flex items-center justify-between">
                <Label>Enabled</Label>
                <Switch
                  checked={selectedSetting.enabled}
                  onCheckedChange={(v) => setSelectedSetting({ ...selectedSetting, enabled: v })}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Min Entry Fee</Label>
                  <Input
                    type="number"
                    value={selectedSetting.min_entry_fee}
                    onChange={(e) => setSelectedSetting({ ...selectedSetting, min_entry_fee: parseFloat(e.target.value) })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Max Entry Fee</Label>
                  <Input
                    type="number"
                    value={selectedSetting.max_entry_fee}
                    onChange={(e) => setSelectedSetting({ ...selectedSetting, max_entry_fee: parseFloat(e.target.value) })}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Rake %</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={selectedSetting.rake_percentage}
                    onChange={(e) => setSelectedSetting({ ...selectedSetting, rake_percentage: parseFloat(e.target.value) })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Rake Cap</Label>
                  <Input
                    type="number"
                    value={selectedSetting.rake_cap}
                    onChange={(e) => setSelectedSetting({ ...selectedSetting, rake_cap: parseFloat(e.target.value) })}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Max Players</Label>
                  <Input
                    type="number"
                    value={selectedSetting.max_players}
                    onChange={(e) => setSelectedSetting({ ...selectedSetting, max_players: parseInt(e.target.value) })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Turn Timer (s)</Label>
                  <Input
                    type="number"
                    value={selectedSetting.turn_timer}
                    onChange={(e) => setSelectedSetting({ ...selectedSetting, turn_timer: parseInt(e.target.value) })}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Drop Penalty</Label>
                <Input
                  type="number"
                  value={selectedSetting.drop_penalty}
                  onChange={(e) => setSelectedSetting({ ...selectedSetting, drop_penalty: parseFloat(e.target.value) })}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialog(false)}>Cancel</Button>
            <Button onClick={handleUpdateSetting} data-testid="save-setting-btn">Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Table Dialog */}
      <Dialog open={createTableDialog} onOpenChange={setCreateTableDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create New Table</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Variant</Label>
              <Select
                value={newTable.variant}
                onValueChange={(v) => setNewTable({ ...newTable, variant: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="points_rummy">Points Rummy</SelectItem>
                  <SelectItem value="pool_101">Pool 101</SelectItem>
                  <SelectItem value="pool_201">Pool 201</SelectItem>
                  <SelectItem value="deals_rummy">Deals Rummy</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Entry Fee</Label>
              <Input
                type="number"
                value={newTable.entry_fee}
                onChange={(e) => setNewTable({ ...newTable, entry_fee: parseFloat(e.target.value) })}
              />
            </div>
            <div className="space-y-2">
              <Label>Max Players</Label>
              <Select
                value={newTable.max_players.toString()}
                onValueChange={(v) => setNewTable({ ...newTable, max_players: parseInt(v) })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="2">2 Players</SelectItem>
                  <SelectItem value="4">4 Players</SelectItem>
                  <SelectItem value="6">6 Players</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between">
              <Label>Private Table</Label>
              <Switch
                checked={newTable.is_private}
                onCheckedChange={(v) => setNewTable({ ...newTable, is_private: v })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateTableDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateTable} data-testid="confirm-create-table-btn">Create Table</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
